import React from "react";
import ContractDashboard, { fromWei } from "./shared/ContractDashboard";
import matrixFunctions from "./functions/matrixFunctions";
import { nemiMatrix, nemiMatrixAbi } from "./Contract";

/**
 * Nemi Matrix — subscriptions, levels, referral tree and matrix income.
 * Every function of the nemiMatrix contract is available through the tabs below.
 */
export default function Page1() {
  const overview = [
    {
      key: "totalUsers",
      label: "Total Users",
      icon: "👥",
      method: "totalUsers",
      color: "#00ff88",
    },
    {
      key: "totalLevel",
      label: "Total Levels",
      icon: "🏆",
      method: "totalLevel",
      color: "#45b7d1",
    },
    {
      key: "myLevel",
      label: "Your Level",
      icon: "🎯",
      color: "#ff00aa",
      compute: async ({ contract, account }) => {
        const info = await contract.methods.userInfo(account).call();
        const exists = await contract.methods.isUserExists(account).call();
        return exists ? String(info.currentLevel ?? info[0]) : "Not joined";
      },
    },
    {
      key: "myDirects",
      label: "Your Directs",
      icon: "🌳",
      color: "#00ccff",
      compute: async ({ contract, account }) => {
        const info = await contract.methods.userInfo(account).call();
        return String(info.directCount ?? info[2]);
      },
    },
    {
      key: "myEarned",
      label: "Your Reward Earned",
      icon: "💰",
      color: "#ffaa00",
      format: (v) => `${v} USDT`,
      compute: async ({ contract, account }) => {
        const info = await contract.methods.userInfo(account).call();
        return fromWei(info.rewardEarned ?? info[3]);
      },
    },
    {
      key: "myLimit",
      label: "Your Holding Limit",
      icon: "📋",
      color: "#6be0d7",
      format: (v) => `${v} NFTs`,
      compute: async ({ contract, account }) =>
        String(await contract.methods.getUserHoldingLimit(account).call()),
    },
    {
      key: "directIncomePercent",
      label: "Direct Income",
      icon: "💵",
      method: "directIncomePercent",
      format: (v) => `${v}%`,
      color: "#00ff88",
    },
    {
      key: "levelIncomePercent",
      label: "Level Income",
      icon: "📊",
      method: "levelIncomePercent",
      format: (v) => `${v}%`,
      color: "#45b7d1",
    },
    {
      key: "platformIncomePercent",
      label: "Platform Income",
      icon: "🏢",
      method: "platformIncomePercent",
      format: (v) => `${v}%`,
      color: "#ff6b6b",
    },
    {
      key: "burnIncomePercent",
      label: "Burn Income",
      icon: "🔥",
      method: "burnIncomePercent",
      format: (v) => `${v}%`,
      color: "#ff8800",
    },
    {
      key: "buyingIncomePercent",
      label: "Buying Income",
      icon: "🛒",
      method: "buyingIncomePercent",
      format: (v) => `${v}%`,
      color: "#ff00aa",
    },
    {
      key: "advancePaymentAmount",
      label: "Advance Payment",
      icon: "💳",
      method: "advancePaymentAmount",
      format: (v) => `${fromWei(v)} USDT`,
      color: "#ffaa00",
    },
    {
      key: "holdingMax",
      label: "Holding Max",
      icon: "📈",
      method: "holdingMax",
      format: (v) => `${v} NFTs`,
      color: "#6be0d7",
    },
    {
      key: "level1Price",
      label: "Level 1 Price",
      icon: "🏅",
      color: "#00ccff",
      format: (v) => `${v} USDT`,
      compute: async ({ contract }) => {
        const plan = await contract.methods.planInfo(1).call();
        return fromWei(plan.planPrice ?? plan[0]);
      },
    },
    {
      key: "contractUsdt",
      label: "Contract USDT",
      icon: "🏦",
      color: "#26a17b",
      format: (v) => `${v} USDT`,
      compute: async ({ getTokenContract, address }) =>
        fromWei(await getTokenContract("USDT").methods.balanceOf(address).call()),
    },
    {
      key: "paused",
      label: "Contract Status",
      icon: "⚡",
      method: "paused",
      format: (v) => (v === true || v === "true" ? "PAUSED" : "ACTIVE"),
      color: "#ff6b6b",
    },
  ];

  return (
    <ContractDashboard
      title="Nemi Matrix"
      subtitle="Subscriptions, levels, referral tree and matrix income distribution"
      icon="🧬"
      accent="#00ff88"
      address={nemiMatrix}
      abi={nemiMatrixAbi}
      functions={matrixFunctions}
      overview={overview}
      approvalTokens={["USDT", "NEMI"]}
    />
  );
}
